import QRCode from "qrcode";

/**
 * Asset helpers for branded export. Everything here resolves images to
 * self-contained data URLs so the dom-to-image capture never hits CORS,
 * cross-origin canvas taint, or external-font issues.
 */

/**
 * Generate a QR code for a URL as a PNG data URL.
 *
 * PNG data URL (not SVG) is deliberate: it embeds cleanly into the raster
 * capture and decodes like any other <img>. Returns null on failure so callers
 * can render the template without a QR rather than breaking the export.
 *
 * @param {string} url
 * @param {number} [size] - Pixel size of the QR image.
 * @returns {Promise<string|null>}
 */
export async function generateQrDataUrl(url, size = 400) {
  if (!url || typeof url !== "string") return null;
  try {
    return await QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: size,
      color: { dark: "#111111", light: "#ffffff" },
    });
  } catch (error) {
    console.warn("[brandedExport] QR generation failed:", error);
    return null;
  }
}

/**
 * Fetch any image URL and return it as a base64 data URL. Returns null on
 * failure (missing/blocked image) so the caller can degrade gracefully.
 *
 * @param {string} url
 * @returns {Promise<string|null>}
 */
export async function fetchImageAsDataUrl(url) {
  if (!url || typeof url !== "string") return null;

  // Already a data URL — nothing to fetch.
  if (url.startsWith("data:")) return url;

  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } catch (error) {
    console.warn("[brandedExport] image fetch failed:", url, error);
    return null;
  }
}

/**
 * Convert a Blob to a base64 data URL.
 * @param {Blob} blob
 * @returns {Promise<string|null>}
 */
export function blobToDataUrl(blob) {
  if (!blob) return Promise.resolve(null);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}
